import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CreditCard, CheckCircle, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const SquarePaymentForm = ({ 
  applicationId, 
  locationId, 
  amount, 
  onPaymentSuccess, 
  onPaymentError,
  isDark = false,
  environment = "production"
}) => {
  const [card, setCard] = useState(null);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const cardContainerRef = useRef(null);
  const initializingRef = useRef(false);

  // Load Square Web Payments SDK
  useEffect(() => {
    if (document.getElementById('square-web-sdk')) {
      setSdkLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.id = 'square-web-sdk';
    script.src = environment === 'sandbox' 
      ? 'https://sandbox.web.squarecdn.com/v1/square.js'
      : 'https://web.squarecdn.com/v1/square.js';
    script.async = true;
    script.onload = () => setSdkLoaded(true);
    script.onerror = () => {
      setError('Failed to load payment system');
      setLoading(false);
    };
    document.body.appendChild(script);
  }, [environment]);

  // Initialize Square Payments
  useEffect(() => {
    if (!sdkLoaded || !applicationId || !locationId || initializingRef.current) return;
    
    const initializeCard = async () => {
      if (initializingRef.current) return;
      initializingRef.current = true;
      
      try {
        if (!window.Square) {
          throw new Error('Square SDK not loaded');
        }

        const paymentsInstance = window.Square.payments(applicationId, locationId);
        setPayments(paymentsInstance);

        const cardInstance = await paymentsInstance.card();
        await cardInstance.attach('#card-container');
        setCard(cardInstance);
        setLoading(false);
      } catch (e) {
        console.error('Square init error:', e);
        setError(e.message || 'Failed to initialize payment form');
        setLoading(false);
      }
    };

    // Small delay to ensure container is ready
    const timer = setTimeout(initializeCard, 100);
    return () => clearTimeout(timer);
  }, [sdkLoaded, applicationId, locationId]);

  const handlePayment = async () => {
    if (!card) {
      toast.error('Payment form not ready');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Tokenize the card
      const result = await card.tokenize();
      
      if (result.status === 'OK') {
        // Send token to backend
        onPaymentSuccess(result.token);
      } else {
        let errorMessage = 'Payment failed';
        if (result.errors && result.errors.length > 0) {
          errorMessage = result.errors.map(e => e.message).join(', ');
        }
        setError(errorMessage);
        onPaymentError(errorMessage);
      }
    } catch (e) {
      console.error('Payment error:', e);
      setError(e.message || 'Payment failed');
      onPaymentError(e.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  if (error && !card) {
    return (
      <Card className={isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-200'}>
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Card Input Container */}
      <div className={`rounded-lg p-4 ${isDark ? 'bg-slate-700' : 'bg-slate-50'}`}>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
          Card Details
        </label>
        
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span className={`ml-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Loading payment form...</span>
          </div>
        )}
        
        <div 
          id="card-container" 
          ref={cardContainerRef}
          className={`min-h-[50px] ${loading ? 'hidden' : ''}`}
          style={{ minHeight: '50px' }}
        />
      </div>

      {error && (
        <div className={`p-3 rounded-lg flex items-center gap-2 ${isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-600'}`}>
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Pay Button */}
      <Button
        onClick={handlePayment}
        disabled={loading || processing || !card}
        className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-6 text-lg"
        data-testid="square-pay-btn"
      >
        {processing ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Pay ${amount?.toFixed(2) || '0.00'}
          </>
        )}
      </Button>

      {/* Supported Cards */}
      <div className="flex items-center justify-center gap-4 pt-2">
        <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
          Accepts Visa, Mastercard, Amex, Discover
        </span>
      </div>

      {/* Security Notice */}
      <p className={`text-xs text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
        🔒 Payments are securely processed by Square
      </p>
    </div>
  );
};

export default SquarePaymentForm;
